/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable prettier/prettier */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import hash from 'object-hash';
import { actions as ciActions, selectors as ciSelectors } from 'store/clientIntegrations';
import { selectors as clientSelectors } from 'store/clients';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import {
  Backdrop,
  Box,
  Button,
  Card,
  color,
  DotProgress,
  FlexBox,
  FormDialog,
  MuiBox,
  SelectDropdown,
  Typography
} from '@kis-technologies/kis-ui';
import MediaTable from '../components/MediaTable';
import ManageImageOverlay from '../components/ManageImageOverlay';
import PreviewImageOverlay from '../components/PreviewImageOverlay';
import { CIMediaProps, MediaResponse, MediaTypes } from '../types';
import { useStyles } from '../styled';

const MEDIA_CONFIG = {
  EVENT: {
    label: 'Event',
    mediaType: MediaTypes.EVENT,
    s3Folder: 'event_images',
    itemsSelector: (state: any) => Object.keys(ciSelectors.selectGroupedEvents(state)),
    selectedSelector: mediaSelectors.selectEvent,
    imagesSelector: mediaSelectors.selectEventImages,
    setSelectedAction: mediaActions.setEvent
  },
  GENRE: {
    label: 'Genre',
    mediaType: MediaTypes.GENRE,
    s3Folder: 'genre_images',
    itemsSelector: ciSelectors.selectGroupedGenres,
    selectedSelector: mediaSelectors.selectGenre,
    imagesSelector: mediaSelectors.selectGenreImages,
    setSelectedAction: mediaActions.setGenre
  },
  VENUE: {
    label: 'Venue',
    mediaType: MediaTypes.VENUE,
    s3Folder: 'venue_images',
    itemsSelector: ciSelectors.selectGroupedVenues,
    selectedSelector: mediaSelectors.selectVenue,
    imagesSelector: mediaSelectors.selectVenueImages,
    setSelectedAction: mediaActions.setVenue,
    requiresImageCheck: true
  }
} as const;
interface UnifiedMediaImagesProps extends CIMediaProps {
  scope: 'EVENT' | 'GENRE' | 'VENUE';
}

const EnvironmentImagesLayout = (props: UnifiedMediaImagesProps) => {
  const { scope, environment } = props;
  const config = MEDIA_CONFIG[scope];
  const classes = useStyles({ genreTab: scope === 'GENRE' })();

  const items = useSelector(config.itemsSelector);
  const selectedItem = useSelector(config.selectedSelector);
  const mediaImages = useSelector(config.imagesSelector);

  const selectPending = useSelector(ciSelectors.selectPending);
  const { error, pending, successful } = useSelector(mediaSelectors.selectAction);
  const clientId = useSelector(clientSelectors.selectClientId);

  const [getTicketingEvents, setSelected, getMedia, deleteMedia, resetAction, downloadMedia] = useActions([
    ciActions.getTicketingEvents,
    config.setSelectedAction,
    mediaActions.getMedia,
    mediaActions.deleteMedia,
    mediaActions.resetAction,
    mediaActions.downloadMedia
  ]);

  const [downloadParams, setDownloadParams] = useState<MediaResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const itemList = Array.isArray(items) ? items : Object.keys(items);
  const hasItems = itemList.length > 0;

  useEffect(() => {
    if (environment) {
      getTicketingEvents(environment.id);
      getMedia({ type: config.mediaType, clientIntegrationId: environment.id });
    }
  }, [environment]);

  useEffect(() => {
    if (downloadParams) downloadMedia(downloadParams.id);
  }, [downloadParams]);

  useEffect(() => {
    if (environment && successful) {
      if (downloadParams) {
        // After downloading, reset the params and action without refreshing media or updating the configuration.
        setDownloadParams(null);
        resetAction();
        return;
      }

      resetAction();
      setShowConfirm(false);
      setShowUpload(false);
      setIsEdit(false);
      getMedia({ type: config.mediaType, clientIntegrationId: environment.id });
    }
  }, [successful]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      resetAction();
      setDownloadParams(null);
    }
  }, [error]);

  useEffect(() => {
    if (!selectedItem || !mediaImages?.length) {
      setPreviewUrl('');
      return;
    }

    const baseUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}/${config.s3Folder}/`;
    const imgObj = mediaImages.find((img) => img.name === hash(selectedItem.replace(/\//g, ' ')));

    setPreviewUrl(imgObj?.s3Name ? baseUrl + imgObj.s3Name : '');
    resetAction();
  }, [selectedItem, mediaImages]);

  const mediaRecords = useMemo<MediaResponse[]>(() => {
    if (!selectedItem || !mediaImages?.length) return [];

    const key = hash(selectedItem.replace(/\//g, ' '));
    const record = mediaImages.find((img) => img.name === key);

    return record ? [record] : [];
  }, [selectedItem, mediaImages]);

  const handleDelete = () => {
    if (mediaRecords[0]?.id) deleteMedia(mediaRecords[0].id);
    setShowConfirm(false);
  };

  return (
    <>
      {showConfirm && (
        <FormDialog
          cancelButtonAction={() => setShowConfirm(false)}
          cancelButtonText="Cancel"
          confirmButtonAction={handleDelete}
          confirmButtonText="Delete"
          open
        >
          <Typography fontSize={24} maxWidth="524px">
            Are you sure you want to delete this image?
          </Typography>
        </FormDialog>
      )}

      {(selectPending || pending) && (
        <Backdrop open>
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}

      {environment && hasItems && (
        <FlexBox direction="column">
          <FlexBox align="center" margin="0 0 6">
            <Typography fontSize={20} fontWeight={700} sx={{ marginRight: '20px' }}>
              <Box width={180}>{config.label}</Box>
            </Typography>

            <SelectDropdown
              items={itemList.sort()}
              itemToString={(item) => item}
              selectedItem={selectedItem}
              onChange={setSelected}
              placeholder={`Select a ${config.label.toLowerCase()}`}
              radius="round"
              sx={{
                boxShadow: 'none',
                height: '45px',
                '& .MuiSelect-select': {
                  color: selectedItem ? '#000' : '#9E9E9E'
                }
              }}
            />
          </FlexBox>

          {!mediaRecords.length && selectedItem && (
            <FlexBox justify="end" margin="0 0 5">
              <Button width={160} onClick={() => setShowUpload(true)}>
                Select Image
              </Button>
            </FlexBox>
          )}

          {selectedItem && (
            <MuiBox className={classes.tableDetailsOuterBox}>
              <Card raised hasBorderRadius>
                <MediaTable
                  mediaRecords={mediaRecords}
                  mediaType={config.mediaType}
                  onPreviewClick={(src) => {
                    setPreviewUrl(src);
                    setPreviewOpen(true);
                  }}
                  onDownloadClick={setDownloadParams}
                  onEditClick={() => {
                    setIsEdit(true);
                    setShowUpload(true);
                  }}
                  onDeleteClick={() => setShowConfirm(true)}
                />
              </Card>
            </MuiBox>
          )}
        </FlexBox>
      )}

      {showUpload && (
        <ManageImageOverlay
          mediaType={config.mediaType}
          resourceName={selectedItem}
          clientIntegrationId={environment?.id}
          action={isEdit ? 'Edit' : 'Select'}
          mediaRecord={isEdit ? mediaRecords[0] : undefined}
          onClose={() => setShowUpload(false)}
        />
      )}

      {previewOpen && previewUrl && <PreviewImageOverlay imageSrc={previewUrl} onClose={() => setPreviewOpen(false)} />}

      {/* ERROR OVERLAY */}
      {errorMessage && (
        <FormDialog
          cancelButtonAction={() => setErrorMessage(null)}
          confirmButtonAction={() => setErrorMessage(null)}
          confirmButtonText="Ok"
          open
        >
          <Typography fontSize={24} maxWidth="524px">
            {errorMessage}
          </Typography>
        </FormDialog>
      )}
    </>
  );
};

export default EnvironmentImagesLayout;
